<script setup lang="ts">
import type { ProductDetails } from "mws-ts-v2-sdk";

import { useEventQuery } from "~/queries/catalog/events";
import { BidAttemptType } from "~/types/Catalog";

type Props = {
  product: ProductDetails;
  isBidLoading: boolean;
  amountInActiveCurrency: number;
  bidAttempt?: BidAttemptType;
  paymentTokenId?: string;
};

const { product, amountInActiveCurrency, isBidLoading, paymentTokenId } =
  defineProps<Props>();

type Emits = {
  (e: "confirm", marketingOptIn?: boolean, paymentTokenId?: string): void;
  (e: "close"): void;
};

const emit = defineEmits<Emits>();

const localePath = useLocalePath();
const optedIn = ref(false); // we start with a default of false

const { partnersMarketingConsent } = useBidder();
const userAlreadyGaveConsentToPartner = computed(() => {
  // we show or hide the "marketing opt-in" checkbox on the `ConfirmBidModal` depending on this
  return hasGivenMarketingConsentToPartner(
    product.partnerId,
    partnersMarketingConsent.value,
  );
});

const onConfirmBid = () => {
  // if the user has already given consent to the partner, we don't want to send the flag to the backend
  // because otherwise the `partnerId` gets added to the `partnersMarketingConsent` array of the user
  // again, which causes duplicates.
  const optIn =
    userAlreadyGaveConsentToPartner.value || !product.partner.name
      ? undefined
      : optedIn.value;

  emit("confirm", optIn, paymentTokenId);
};

const {
  data: bidCosts,
  status: bidCostsStatus,
  isFetching,
  providedFilters,
} = useBidCosts({
  product: () => product,
  bidAmount: () => amountInActiveCurrency,
});

// Set a timeout to enable the button after 300ms
const isInitialConfirmLoading = ref(true);
onMounted(() => {
  setTimeout(() => {
    isInitialConfirmLoading.value = false;
  }, 300);
});

const eventIdentifier = computed(() => product.eventId);
const { data: event } = useEventQuery(eventIdentifier, {
  enabled: !!eventIdentifier.value,
});

const { eventName } = useEventDetails(() => event.value);

const isBidButtonDisabled = computed(
  () => isFetching.value || isBidLoading || bidCostsStatus.value === "error",
);
</script>

<template>
  <BaseModal
    size="md"
    data-cy="bid-confirm"
    @close="$emit('close')"
  >
    <template #header>
      {{
        bidAttempt === BidAttemptType.Maxbid
          ? $t("product.bidConfirm.maxBid.header")
          : $t("product.bidConfirm.header")
      }}
    </template>
    <ProductBidConfirmReservationMessage
      v-if="
        !!paymentTokenId && bidCosts?.preAuthorisationResponse?.reservedAmount
      "
      :selected-payment-token-id="paymentTokenId"
      :reservation-amount="bidCosts?.preAuthorisationResponse?.reservedAmount"
    />
    <ProductCosts
      v-if="bidCosts"
      :bid="{
        costs: bidCosts.bid,
        productName: product.name,
        eventName: eventName,
        bidType: bidAttempt,
      }"
      :buyers-premium="{
        costs: bidCosts.buyersPremium,
        percentage: bidCosts.buyersPremiumPercentage,
      }"
      :vat="{
        costs: bidCosts.vat,
        percentage: bidCosts.vatPercentage,
        itemVatExplanation: bidCosts.itemVatExplanation,
        handlingVatExplanation: bidCosts.handlingVatExplanation,
        vatNumberCheckStatus: bidCosts.vatNumberCheckStatus,
      }"
      :shipping="{
        costs: bidCosts.estimatedShippingCost,
        officeName: bidCosts.shippingOfficeName,
        officeCountryCode: bidCosts.shippingOfficeCountryCode,
      }"
      :total="{ costs: bidCosts.totalPrice }"
      :options="{
        priceVariant:
          bidAttempt === BidAttemptType.Maxbid ? 'max' : 'estimated',
        isShippingCountryProvided: Boolean(
          providedFilters.destinationCountryCode,
        ),
      }"
    />
    <BaseLoadingSpinner v-if="bidCostsStatus === 'pending'" />
    <I18nT
      v-else-if="bidCostsStatus === 'success'"
      scope="global"
      keypath="product.bidConfirm.description"
      class="mt-4 text-xs text-platform-text-3 leading-normal"
      tag="div"
    >
      <template #PLACEHOLDER_amount>
        <BaseCurrency
          :iso="providedFilters.currency"
          :value="amountInActiveCurrency"
          :show-decimals="true"
        />
      </template>
      <template #PLACEHOLDER_minutes>3</template>
      <template #PLACEHOLDER_termsAndConditionsLink>
        <NuxtLink
          class="font-light underline"
          :to="localePath({ path: '/terms-and-conditions' })"
        >
          <strong>{{ $t("common.termsAndConditions") }}</strong>
        </NuxtLink>
      </template>
    </I18nT>

    <I18nT
      v-else
      scope="global"
      keypath="product.costs.error"
      class="mt-4 text-xs text-platform-text-3 leading-normal"
      tag="div"
    >
      <template #PLACEHOLDER_contactSupport>
        <BaseCustomerSupportLink class="font-light underline">
          <strong>{{ $t("common.contactSupport") }}</strong>
        </BaseCustomerSupportLink>
      </template>
    </I18nT>

    <FormKit
      v-if="
        product.partner.name &&
        !userAlreadyGaveConsentToPartner &&
        bidCostsStatus === 'success'
      "
      id="bid-marketing-opt-in"
      v-model="optedIn"
      type="checkbox"
      outer-class="mt-4"
    >
      <template #label>
        <I18nT
          scope="global"
          keypath="product.bidConfirm.optIn"
          tag="span"
          class="cursor-pointer text-sm"
        >
          <template #vendor>
            <strong>{{ product.partner.name }}</strong>
          </template>
        </I18nT>
      </template>
    </FormKit>
    <template #footer>
      <BaseButton
        id="bid-cancel-button"
        color-scheme="secondary"
        data-cy="bid-confirm-cancel"
        @click="$emit('close')"
      >
        {{ $t(`product.bidConfirm.cancel`) }}
      </BaseButton>
      <BaseButton
        v-bind="$attrs"
        id="bid-confirm-button"
        data-cy="bid-confirm-button"
        :disabled="isBidButtonDisabled"
        :is-loading="isInitialConfirmLoading || isBidLoading"
        color-scheme="primary"
        :cta="true"
        @click="onConfirmBid"
      >
        <p>
          {{
            !!paymentTokenId
              ? $t("product.bidConfirm.confirmReservation")
              : $t("product.bidConfirm.confirm")
          }}
        </p>
      </BaseButton>
    </template>
  </BaseModal>
</template>

<style lang="postcss" scoped>
.bid-confirm-modal {
  &__button {
    :deep(.icon) {
      @apply text-white;
    }
  }
}
</style>
